import React, { SetStateAction, useLayoutEffect, useReducer } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

import clsx from 'clsx';
import { ErrorBoundary } from 'react-error-boundary';

import useLocalStorageState from '@watershed/shared-frontend/hooks/useLocalStorageState';
import { useResizeObserverWithPredicate } from '@watershed/ui-core/hooks/useResizeObserverWithPredicate';
import {
  SIDEBAR_BREAKPOINT_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from '@watershed/shared-universal/utils/constants';
import usePrevious from '@watershed/ui-core/hooks/usePrevious';

import { ErrorFallback } from '../components/ErrorFallback';
import { useIsSideBarExpandAutoCollapsed } from '../components/navigation/atoms';
import SideBar from '../components/navigation/SideBar';
import { SkipNavContent } from '../components/navigation/SkipNav';
import { useIsOnFinancePage } from '../utils/CurrentProductContext';
import { FinanceSnapshotsContextProvider } from '../utils/finance/FinanceSnapshotsContext';

const rootSx: SxProps<Theme> = {
  minWidth: '100%',
  width: 'fit-content',
  minHeight: '100vh',
  margin: '0',
  padding: '0',
  display: 'grid',
  gridTemplateColumns: `${SIDEBAR_EXPANDED_WIDTH}px 1fr`,
  maxWidth: '100vw',
  backgroundColor: (theme) => theme.palette.grey10,
  '&.is-collapsed': {
    gridTemplateColumns: `${SIDEBAR_COLLAPSED_WIDTH}px 1fr`,
  },
  '@media print': { display: 'block' },
};

const contentSx: SxProps<Theme> = {
  width: '100%',
  minHeight: '100vh',
  backgroundColor: (theme) => theme.palette.background.paper,
  gridColumn: 2,
  display: 'flex',
  flexDirection: 'column',
  // Setting overflow: hidden here allows complex pages to implement their
  // own scrolling regions without interference.
  overflow: 'hidden',
  '&.is-right-sidebar-open': {
    // Enabling the content to be scrollable if it exceeds the root viewport height.
    // This avoids the double scrollbar jank.
    overflow: 'auto',
    // Adjusted the background color, as with the height being fixed at 100dvh
    // the PageContainer background color stops at the bottom of the viewport.
    // TODO: Check for funkiness in other layouts.
    backgroundColor: (theme) => theme.palette.grey05,
  },
};

type SidebarAction =
  | { type: 'set-collapse'; payload: { value: boolean } }
  | {
      type: 'window-resize-collapse';
      payload: { value: boolean };
    };

interface SidebarState {
  isCollapsed: boolean;
  hasManuallyControlled: boolean;
}

const SIDEBAR_STORAGE_KEY = 'watershed-sidebar-state';

interface StoredSidebarState {
  hasManuallyControlled: boolean;
  isCollapsed: boolean;
}

function sidebarReducer(
  state: SidebarState,
  action: SidebarAction
): SidebarState {
  switch (action.type) {
    case 'set-collapse':
      return {
        isCollapsed: action.payload.value,
        hasManuallyControlled: true,
      };
    case 'window-resize-collapse':
      if (state.isCollapsed !== action.payload.value) {
        return {
          isCollapsed: action.payload.value,
          hasManuallyControlled: false,
        };
      }
      return state;
    default:
      return state;
  }
}

function useSidebarState() {
  const [isSideBarExpandAutoCollapsed, setIsSideBarExpandAutoCollapsed] =
    useIsSideBarExpandAutoCollapsed();

  const predicate = (entry: ResizeObserverEntry) =>
    entry.contentRect.width < SIDEBAR_BREAKPOINT_WIDTH;

  const currentIsCollapsedDueToResize =
    useResizeObserverWithPredicate(predicate);
  const previousIsCollapsedDueToResize = usePrevious(
    currentIsCollapsedDueToResize
  );

  // Use localStorage to persist manual sidebar state across page navigations
  const [storedState, setStoredState] =
    useLocalStorageState<StoredSidebarState | null>(SIDEBAR_STORAGE_KEY, null);

  const initialState: SidebarState = storedState
    ? {
        isCollapsed: storedState.isCollapsed,
        hasManuallyControlled: storedState.hasManuallyControlled,
      }
    : {
        isCollapsed: currentIsCollapsedDueToResize,
        hasManuallyControlled: false,
      };

  const [{ isCollapsed: isCollapsedState, hasManuallyControlled }, dispatch] =
    useReducer(sidebarReducer, initialState);

  // Shared function to update DOM state when sidebar collapse changes
  const updateSidebarDOMState = (collapsed: boolean) => {
    document.body.classList.toggle('sidebar-collapsed-yes', collapsed);
    document.body.classList.toggle('sidebar-collapsed-no', !collapsed);
    // Set CSS custom property for sidebar width
    document.documentElement.style.setProperty(
      '--sidebar-width',
      `${collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH}px`
    );
    // Emit custom event for hooks that need to observe sidebar state
    document.dispatchEvent(
      new CustomEvent('sidebar-collapse-change', {
        detail: { isCollapsed: collapsed },
      })
    );
  };

  // Update DOM state on mount when there's stored state
  useLayoutEffect(() => {
    if (storedState) {
      updateSidebarDOMState(isCollapsedState);
    }
  }, [storedState, isCollapsedState]);

  useLayoutEffect(() => {
    if (
      currentIsCollapsedDueToResize !== previousIsCollapsedDueToResize &&
      !hasManuallyControlled
    ) {
      dispatch({
        type: 'window-resize-collapse',
        payload: { value: currentIsCollapsedDueToResize },
      });
      updateSidebarDOMState(currentIsCollapsedDueToResize);
    } else if (hasManuallyControlled) {
      updateSidebarDOMState(isCollapsedState);
    }
  }, [
    currentIsCollapsedDueToResize,
    previousIsCollapsedDueToResize,
    hasManuallyControlled,
    isCollapsedState,
  ]);

  // If manually controlled, use that state; otherwise use auto-collapse or reducer state
  const isCollapsed = hasManuallyControlled
    ? isCollapsedState
    : isCollapsedState || isSideBarExpandAutoCollapsed;

  const setIsCollapsed = (value: SetStateAction<boolean>) => {
    setIsSideBarExpandAutoCollapsed(false);
    const collapsed = typeof value === 'function' ? value(isCollapsed) : value;
    dispatch({
      type: 'set-collapse',
      payload: { value: collapsed },
    });
    updateSidebarDOMState(collapsed);

    // Only persist if the user is collapsing (not expanding)
    // Expanding is temporary for the current page only
    if (collapsed) {
      setStoredState({
        hasManuallyControlled: true,
        isCollapsed: true,
      });
    } else {
      // Clear storage when manually expanding - don't persist expanded state
      setStoredState(null);
    }
  };

  return {
    isCollapsed,
    setIsCollapsed,
  };
}

export default function SidebarLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isCollapsed, setIsCollapsed } = useSidebarState();
  const isOnFinancePage = useIsOnFinancePage();

  const innerContent = (
    <Box className={clsx(isCollapsed && 'is-collapsed')} sx={rootSx}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <SideBar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      </ErrorBoundary>
      <SkipNavContent />
      <Box component="main" sx={contentSx}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {children}
        </ErrorBoundary>
      </Box>
    </Box>
  );

  const contentWithProvider = isOnFinancePage ? (
    <FinanceSnapshotsContextProvider>
      {innerContent}
    </FinanceSnapshotsContextProvider>
  ) : (
    innerContent
  );
  return contentWithProvider;
}
